<template>
  <div>
    <link crossorigin="anonymous" href="https://use.fontawesome.com/releases/v5.8.1/css/all.css" integrity="sha384-50oBUHEmvpQ+1lW4y57PTFmhCaXp0ML5d60M1M7uH2+nqUivzIebhndOJK28anvf" rel="stylesheet">
    <div class="card__wrapper">
      <div class="card">
        <h1 class="card__title">{{ $t('messages.create') }}</h1>
        <div class="card__description">
          <b-form @submit.prevent="create">
            <b-form-group id="titleGroup"
                          label-for="name"
                          v-bind:label="$t('messages.title')+':'">
              <vue-bootstrap-typeahead :data="searchResults" :serializer="s => s.name" @hit="select" v-model="search" />
            </b-form-group>
            <b-form-group id="imageGroup"
                          label-for="image"
                          v-bind:label="$t('messages.image')+':'" class="position-relative d-inline-block">
              <b-img :alt="form.title" :src="form.image" fluid id="image" class="image" name="image" v-if="selectedSearch && form.image"></b-img>
              <div class="position-absolute left-image-button" v-if="selectedSearch && !firstImage">
                <font-awesome-icon icon="circle" class="text-white left-circle-bg"></font-awesome-icon>
              </div>
              <div class="position-absolute left-image-button" v-if="selectedSearch && !firstImage">
                <font-awesome-icon icon="chevron-circle-left" class="red pointer" size="2x" @click="previousImage"></font-awesome-icon>
              </div>
              <div class="position-absolute right-image-button" v-if="selectedSearch && !lastImage">
                <font-awesome-icon icon="circle" class="text-white right-circle-bg"></font-awesome-icon>
              </div>
              <div class="position-absolute right-image-button" v-if="selectedSearch && !lastImage">
                <font-awesome-icon icon="chevron-circle-right" class="red pointer" size="2x" @click="nextImage"></font-awesome-icon>
              </div>
              <p class="font-italic" v-if="!selectedSearch || (selectedSearch && !form.image)">{{ $t('messages.image_none') }}</p>
            </b-form-group>
            <b-form-group id="newImageGroup"
                          v-bind:label="$t('messages.product_image_new')+':'"
                          label-for="newImage">
              <b-form-file id="newImage" name="newImage" accept=".png, .jpg"
                           v-model="image"
                           :placeholder="$t('errors.product_image_new')"
                           :browse-text="$t('messages.browse')" :disabled="!selectedSearch"></b-form-file>
            </b-form-group>
            <b-form-group id="languageGroup"
                          :label="$t('messages.language')+':'">
              <b-form-checkbox v-for="language in languages" :key="'lang-' + language" :value="language"
                               v-model="form.language" :disabled="!selectedSearch">
                {{ $t('languages.' + language) }}
              </b-form-checkbox>
            </b-form-group>
            <b-form-group id="eanGroup"
                          :label="$t('messages.ean')+':'"
                          label-for="ean">
              <b-form-input id="ean" name="ean"
                            v-model="form.ean" :disabled="!selectedSearch"></b-form-input>
            </b-form-group>
            <b-form-group id="descriptionGroup"
                          label-for="description"
                          v-bind:label="$t('messages.description')+':'">
              <ckeditor :config="editor.config" :disabled="!selectedSearch" :editor="editor.instance" :key="editor.update"
                        id="description" name="description" v-model="editor.data"></ckeditor>
            </b-form-group>
            <b-form-group id="urlGroup"
                          label-for="url"
                          v-bind:label="$t('messages.url')+':'">
              <b-form-input :disabled="true" id="url" name="url" type="text"
                            v-model.trim="form.url"></b-form-input>
            </b-form-group>
            <b-form-group id="publisherGroup"
                          label-for="publisher"
                          v-bind:label="$t('messages.publisher')+':'">
              <b-form-select :disabled="!selectedSearch" v-model="form.publisher">
                <option :key="'pub-' + publisher" :value="publisher" v-for="publisher in form.publishers">{{ publisher }}</option>
              </b-form-select>
            </b-form-group>
            <b-form-group id="videoGroup"
                          v-if="form.videos && form.videos.length > 0"
                          label-for="video"
                          v-bind:label="$t('messages.video')+':'">
              <b-form-select :disabled="!selectedSearch" v-model="form.video">
                <option :key="'vid-' + video.url" :value="video.url" v-for="video in form.videos">{{ video.name }}</option>
              </b-form-select>
              <div class="video mt-2">
                <b-embed v-if="form.video" type="iframe" class="mt-2" aspect="16by9" :src="embedVideo" allowfullscreen></b-embed>
              </div>
            </b-form-group>
            <div class="d-flex">
              <b-form-group id="preorderGroup" class="mr-2">
                <b-form-checkbox :disabled="!selectedSearch" class="checkboxNoMargin"
                                 id="preorder"
                                 name="preorder" v-model="form.preorder">&nbsp;</b-form-checkbox>
                <span @click="click('preorder')" class="checkboxLineHeight pointer">{{ $t('messages.preorder_check') }}</span>
              </b-form-group>
              <b-form-group id="backorderGroup">
                <b-form-checkbox :disabled="!selectedSearch" class="checkboxNoMargin"
                                 id="backorder"
                                 name="backorder" v-model="form.backorder">&nbsp;</b-form-checkbox>
                <span @click="click('backorder')" class="checkboxLineHeight pointer">{{ $t('messages.backorder_check') }}</span>
              </b-form-group>
            </div>
            <b-form-group id="expectedDateGroup"
                          label-for="expectedDate"
                          v-bind:label="$t('messages.expected_date')+':'"
                          v-if="form.preorder || form.backorder">
              <date-picker :config="expectedDateOptions" :disabled="!selectedSearch" @input="$v.form.expectedDate.$touch" id="expectedDate"
                           name="expectedDate" v-model="form.expectedDate"></date-picker>
            </b-form-group>
            <b-form-group id="stockGroup"
                          :label="$t('messages.stock')+':'"
                          label-for="stock"
                          :invalid-feedback="$t('errors.stock')" v-if="!form.preorder && !form.backorder">
              <b-form-input :disabled="!selectedSearch" id="stock" name="stock"
                            min="0" :number="true" type="number"
                            v-model="form.stock"
                            @input="$v.form.stock.$touch"
                            :state="$v.form.stock.$dirty ? !$v.form.stock.$error : null"></b-form-input>
            </b-form-group>
            <b-form-group id="priceGroup"
                          :label="$t('messages.price')+':'"
                          label-for="price"
                          :invalid-feedback="!$v.form.price.decimalTwoRounds ? $t('errors.price_valid') : $t('errors.price')">
              <b-form-input :disabled="!selectedSearch" id="price" name="price"
                            min="0" :number="true" type="number" step="0.01"
                            v-model="form.price"
                            @input="$v.form.price.$touch"
                            :state="$v.form.price.$dirty ? !$v.form.price.$error : null"></b-form-input>
            </b-form-group>
            <b-form-group id="reducedPriceGroup"
                          :label="$t('messages.reduced_price')+':'"
                          label-for="reducedPrice"
                          :invalid-feedback="!$v.form.reducedPrice.decimalTwoRounds ? $t('errors.reduced_price_valid') : $t('errors.reduced_price')">
              <b-form-input :disabled="!selectedSearch" id="reducedPrice" name="reducedPrice"
                            min="0" :number="true" type="number" step="0.01"
                            v-model="form.reducedPrice"
                            @input="$v.form.reducedPrice.$touch"
                            :state="$v.form.reducedPrice.$dirty ? !$v.form.reducedPrice.$error : null"></b-form-input>
            </b-form-group>
            <notification-button :disabled="$v.form.$invalid" :message="$t('messages.create')" :notification="notifications.create"
                                 field="create"></notification-button>
          </b-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required, requiredIf, decimal, minValue } from 'vuelidate/lib/validators'
import NotificationButton from '../../components/NotificationButton'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'

import DatePicker from 'vue-bootstrap-datetimepicker'

import { defaultMixin } from '../../mixin/default'
import { notificationMixin } from '../../mixin/notification'

import { productService } from '../../services/product.service'
import { bggService } from '../../services/bgg.service'

import _ from 'lodash'

import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css'

const decimalTwoRounds = (value) => {
  return value !== null && /^[-]?\d*(\.\d{0,2})?$/.test(value)
}

export default {
  name: 'createproduct',
  metaInfo: function () {
    return {
      title: this.$i18n.t('messages.create') + ' - CrowdFinder',
      meta: [
        {
          'property': 'og:title',
          'content': this.$i18n.t('messages.create'),
          'vmid': 'og:title'
        }
      ]
    }
  },
  components: { NotificationButton, DatePicker },
  data: function () {
    return {
      languages: productService.getLanguages(),
      form: {
        id: null,
        title: null,
        ean: null,
        url: null,
        image: null,
        images: [],
        language: [],
        description: null,
        descriptionBgg: null,
        year: null,
        minPlayers: null,
        maxPlayers: null,
        bestWith: null,
        age: null,
        languageDependencyLevel: null,
        minPlayTime: null,
        maxPlayTime: null,
        designer: null,
        publishers: [],
        publisher: null,
        videos: [],
        video: null,
        rating: null,
        expectedDate: null,
        preorder: true,
        backorder: false,
        stock: null,
        price: null,
        reducedPrice: null
      },
      image: null,
      search: '',
      selectedSearch: null,
      searchResults: [],
      editor: {
        instance: ClassicEditor,
        data: '',
        config: {
          toolbar: [ 'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', 'insertTable', 'undo', 'redo' ],
          language: this.$i18n.locale
        },
        update: 0
      },
      expectedDateOptions: {
        format: 'MM-YYYY',
        locale: this.$i18n.locale
      },
      notifications: {
        create: null
      }
    }
  },
  computed: {
    firstImage: function () {
      return this.form.images.indexOf(this.form.image) === 0
    },
    lastImage: function () {
      return this.form.images.indexOf(this.form.image) === (this.form.images.length - 1)
    },
    embedVideo: function () {
      return this.form.video.replace('watch?v=', 'embed/')
    }
  },
  mixins: [ validationMixin, defaultMixin, notificationMixin ],
  validations: {
    form: {
      image: {
        required: requiredIf(function () {
          return !this.image
        })
      },
      language: {
        required
      },
      publisher: {
        required
      },
      expectedDate: {
        required: requiredIf(function (value) {
          return this.form.preorder || this.form.backorder
        })
      },
      stock: {
        required: requiredIf(function (value) {
          return !this.form.preorder && !this.form.backorder
        }),
        minValue: minValue(0)
      },
      price: {
        required,
        decimal,
        decimalTwoRounds,
        minValue: minValue(0)
      },
      reducedPrice: {
        required,
        decimal,
        decimalTwoRounds,
        minValue: minValue(0)
      }
    }
  },
  methods: {
    select: function (event) {
      let store = this.$store
      let loader = this.$loading.show()

      this.selectedSearch = event
      if (this.selectedSearch) {
        bggService.get(this.selectedSearch.id)
          .then((result) => {
            this.form.id = result.id
            this.form.title = this.selectedSearch.name
            this.form.url = result.url
            this.form.images = result.images
            this.form.image = result.images[0]
            this.form.descriptionBgg = result.description
            this.form.year = result.year
            this.form.minPlayers = result.minPlayers
            this.form.maxPlayers = result.maxPlayers
            this.form.bestWith = result.bestWith
            this.form.age = result.age
            this.form.languageDependencyLevel = result.languageDependencyLevel
            this.form.minPlayTime = result.minPlayTime
            this.form.maxPlayTime = result.maxPlayTime
            this.form.designer = result.designer
            this.form.publishers = result.publishers
            this.form.publisher = null
            this.form.videos = result.videos
            this.form.video = null
            this.form.rating = result.rating

            this.form.language = []
            this.form.ean = null
            this.form.description = ''
            this.form.expectedDate = null
            this.form.preorder = true
            this.form.backorder = false
            this.form.stock = null
            this.form.price = null
            this.form.reducedPrice = null

            this.editor.data = this.form.description

            loader.hide()
          }, () => {
            store.commit('error', 'errors.search')
          })
      }
    },
    previousImage: function () {
      this.form.image = this.form.images[this.form.images.indexOf(this.form.image) - 1]
    },
    nextImage: function () {
      this.form.image = this.form.images[this.form.images.indexOf(this.form.image) + 1]
    },
    click: function (id) {
      document.getElementById(id).click()
    },
    create: function () {
      if (!this.$v.form.$invalid) {
        let product = this.$v.form.$model
        let store = this.$store
        let router = this.$router

        let formData = new FormData()
        formData.append('image', this.image)
        formData.append('product', JSON.stringify(product))

        productService.create(formData)
          .then((response) => {
            router.push('/product/' + this.slugText(response.id + '/' + response.name))
            setTimeout(() => store.commit('success', 'success.create_product'))
          }, () => {
            store.commit('error', 'errors.create_product')
            this.sendNotification('create', 'error')
          })
      }
    }
  },
  created: function () {
    this.$root.$on('updateLocale', () => {
      this.expectedDateOptions.locale = this.$i18n.locale
      this.editor.config.language = this.$i18n.locale
      this.editor.update += 1
    })

    this.$root.$on('resetNotification', (notification) => {
      this.resetNotification(notification)
    })
  },
  watch: {
    'form.preorder': function () {
      if (!this.form.preorder && !this.form.backorder) {
        this.form.expectedDate = null
      }
      if (this.form.preorder) {
        this.form.backorder = false
      }
    },
    'form.backorder': function () {
      if (!this.form.preorder && !this.form.backorder) {
        this.form.expectedDate = null
      }
      if (this.form.backorder) {
        this.form.preorder = false
      }
    },
    search: _.debounce(function () {
      let store = this.$store

      if (this.search) {
        bggService.search(this.search)
          .then((results) => {
            this.searchResults = results
          }, () => {
            store.commit('error', 'errors.search')
          })
      }
      this.form.title = this.search
    }, 500),
    'editor.data': function () {
      this.form.description = this.editor.data
    }
  },
  beforeDestroy: function () {
    this.$root.$off('updateLocale')
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .buttonNoPadding {
    padding: 0px 0px 1.5px;
    font-size: inherit;
  }

  .left-circle-bg {
    width: 32px;
    height: 32px;
    margin-top: -2px;
    margin-left: -2px;
  }

  .right-circle-bg {
    width: 32px;
    height: 32px;
    margin-top: -2px;
    margin-right: -2px;
  }

  .left-image-button {
    top: 50%;
    left: 10px;
  }

  .right-image-button {
    top: 50%;
    right: 10px;
  }

  .video {
    max-width: 680px;
  }

  @media screen and (min-width: 768px) {
    .image {
      max-width: 680px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/product/CreateProduct.vue